import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Badge } from './ui/badge';
import {
  HomeIcon,
  SettingsIcon,
  UsersIcon,
  UserIcon,
  FileTextIcon,
  TreePineIcon,
  FolderIcon,
  ShieldIcon,
  SearchIcon,
  XIcon,
  BuildingIcon,
  DollarSign,
  FlaskConicalIcon,
  SparklesIcon,
  Palette as PaletteIcon,
  CreditCard as CreditCardIcon,
} from 'lucide-react';

// Import Settings Components
import GeneralSettings from './GeneralSettings';
import SecuritySettings from './SecuritySettings';
import SandboxModeSettings from './SandboxModeSettings';
import SoloDemoDataSettings from './SoloDemoDataSettings';
import AdvanceRulesSettings from './AdvanceRulesSettings';
import UserManagementSettings from './UserManagementSettings';
import UserTypesSettings from './UserTypesSettings';
import ApplicationSettings from './ApplicationSettings';
import SimpleRoleHierarchy from './SimpleRoleHierarchy';
import UserPermissionsSettings from './UserPermissionsSettings';
import UserPermissions from './UserPermissions';
import PaymentPermissions from './PaymentPermissions';
import Library from './Library';
import CostCenters from './CostCenters';
import CategoryManagement from './CategoryManagement';
import AdvanceCategoriesSettings from './AdvanceCategoriesSettings';
import SupplierManagementSettings from './SupplierManagementSettings';
import ExpenseCategoryManagement from './ExpenseCategoryManagement';
import DepartmentManagement from './DepartmentManagement';
import BrandingSettings from './BrandingSettings';
import CreditCardsSettings from './CreditCardsSettings';

// ============================================================================
// CATEGORY_FEATURE_MAP — Ayarlar sidebar'ındaki kategorileri, Ultra Admin
// "Özellik Yönetimi" panelinde tanımlı feature slug'lara bağlar.
// Bir kategoride mapping varsa VE kullanıcının plan_features listesinde o
// feature yoksa → kategori gizlenir. Mapping olmayan kategoriler bu matrix'ten
// etkilenmez (kendi checkleri geçerli kalır).
//
// Ultra Admin bir plan'a feature ekleyip/çıkardığında tüm bağlı kategoriler
// dinamik olarak açılır/kapanır — deploy gerektirmez.
// ============================================================================
const CATEGORY_FEATURE_MAP = {
  'user-management':        'user_management',
  'role-hierarchy':         'role_hierarchy',
  'user-permissions':       'user_permissions',
};

// Solo planı tek kullanıcılı olduğu için ekip / hiyerarşi / yetki paylaşımı
// gibi tüm çok-kullanıcı kategorileri Solo'da her zaman gizli. Bu set feature
// map'inden bağımsız çalışır — Ultra Admin bir feature Solo'ya eklese bile
// bu kategoriler Solo UI'da açılmaz.
const SOLO_HIDDEN_CATEGORIES = new Set([
  'advance-rules',
  'user-management',
  'user-types',
  'role-hierarchy',
  'department-management',
  'user-permissions',
  'document-permissions',
  'payment-permissions',
]);

const Settings = () => {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('profile');
  const [searchQuery, setSearchQuery] = useState('');

  // Check if user has admin permissions
  const hasAdminPermissions = () => {
    if (!user?.role) return false;
    const roleName = user.role.name;
    return roleName === 'Süper Admin' || 
           roleName === 'Super Admin' || 
           roleName === 'Admin';
  };

  // Check if user is on Starter plan (no advance rules)
  const isStarterPlan = () => {
    const plan = user?.company?.selected_plan || user?.company?.subscription_plan;
    return plan === 'starter';
  };

  // Check if user is on Solo plan (single-user, no team/approval/permission management)
  const isSoloPlan = () => {
    const plan = user?.company?.selected_plan || user?.company?.subscription_plan;
    return plan === 'solo';
  };

  // Feature-driven gating for sidebar categories. Ultra Admin panelindeki
  // "Özellik Yönetimi" bir plan'a ilgili feature'ı verdiyse kategori açılır.
  // Solo, tek kullanıcılı bir plan olduğu için SOLO_HIDDEN_CATEGORIES setindeki
  // tüm kategoriler Solo'da her zaman gizli — bu set feature map'inden
  // bağımsız çalışır (Solo'daki eski davranışı bire bir korur).
  const isCategoryHiddenByPlan = (categoryId) => {
    if (isSoloPlan() && SOLO_HIDDEN_CATEGORIES.has(categoryId)) return true;
    const featureSlug = CATEGORY_FEATURE_MAP[categoryId];
    if (!featureSlug) return false;                       // mapping yok → asla gizleme
    const planFeatures = user?.plan_features || [];
    return !planFeatures.includes(featureSlug);
  };

  // Check if user is from mbucak@gmail.com's company
  const isMbucakCompany = () => {
    // Check if company owner email is mbucak@gmail.com
    const ownerEmail = user?.company?.owner_email || '';
    return ownerEmail === 'mbucak@gmail.com';
  };

  // Note: Credit card features are now available to all users via Settings

  // Settings categories
  const settingsCategories = [
    {
      id: 'profile',
      name: 'Profil Ayarları',
      description: 'Kişisel bilgiler ve hesap ayarları',
      keywords: ['profil', 'isim', 'ad', 'soyad', 'email', 'e-posta', 'telefon', 'kişisel', 'bilgiler', 'hesap', 'hesabımı sil', 'sil', 'kapat', 'delete', 'account'],
      icon: UserIcon,
      available: true
    },
    {
      id: 'security',
      name: 'Güvenlik Ayarları',
      description: 'Şifre değiştirme ve güvenlik seçenekleri',
      keywords: ['güvenlik', 'şifre', 'password', 'parola', 'değiştir', 'değiştirme', 'güncelle', 'oturum', 'giriş', 'login', 'logout', 'çıkış'],
      icon: ShieldIcon,
      available: true
    },
    {
      id: 'sandbox-mode',
      name: 'Sandbox Modu',
      description: 'Test/deneme amaçlı görsel uyarıyı aç/kapat',
      keywords: ['sandbox', 'test', 'deneme', 'mod', 'banner', 'rozet', 'experiment'],
      icon: FlaskConicalIcon,
      // Solo planı tek kullanıcılı ve prod-oriented — sandbox modunun Solo'da
      // anlamı yok, kategoriyi gizle.
      available: !isSoloPlan()
    },
    {
      id: 'solo-demo-data',
      name: 'Örnek Veriler',
      description: 'Solo hesabınıza otomatik eklenen örnek verileri yönetin',
      keywords: ['örnek', 'demo', 'veri', 'seed', 'temizle', 'sil', 'reset', 'sample'],
      icon: SparklesIcon,
      // Dashboard'da zaten "Örnek verilerle başla" butonu var — Ayarlar
      // altında tekrar etmesine gerek yok. Kategoriyi tamamen kaldır.
      available: false
    },
    {
      id: 'credit-cards',
      name: 'Kredi Kartları',
      description: 'Bankalarınızı ve kartlarınızı yönetin',
      keywords: ['kart', 'kredi', 'banka', 'credit', 'card', 'bank', 'ödeme'],
      icon: CreditCardIcon,
      // Solo için — küçük hesaplar cari/kart yönetimini basit tek yerde ister.
      available: isSoloPlan(),
    },
    {
      id: 'company',
      name: 'Şirket Bilgileri', 
      description: 'Şirket detayları ve abonelik bilgileri',
      keywords: ['şirket', 'firma', 'abonelik', 'paket', 'ödeme', 'fatura', 'vergi', 'adres', 'plan', 'upgrade', 'yükselt', 'iptal', 'cancel'],
      icon: HomeIcon,
      available: true
    },
    {
      id: 'advance-rules',
      name: 'Avans Kuralları',
      description: 'Avans kapama süreleri ve kuralları',
      keywords: ['avans', 'kural', 'süre', 'kapama', 'limit', 'onay', 'approval', 'close', 'gün', 'tarih'],
      icon: SettingsIcon,
      // Avans Kuralları menüsü sadece "Gelişmiş" avans mode'unda anlamlı;
      // Basit mode'da tüm kural mekanizması devre dışı olduğu için gizli.
      // Solo planı ise SOLO_HIDDEN_CATEGORIES üzerinden hard-hidden.
      available: hasAdminPermissions()
        && !(isSoloPlan() && SOLO_HIDDEN_CATEGORIES.has('advance-rules'))
        && user?.advance_mode !== 'simple',
      adminOnly: true
    },
    {
      id: 'user-management',
      name: 'Kullanıcı Yönetimi',
      description: 'Takım üyeleri ve kullanıcı limitleri',
      keywords: ['kullanıcı', 'ekle', 'sil', 'düzenle', 'takım', 'üye', 'davet', 'invite', 'limit', 'user', 'add', 'remove', 'edit', 'member'],
      icon: UsersIcon,
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('user-management'),
      adminOnly: true
    },
    {
      id: 'user-types',
      name: 'Kullanıcı Pozisyonları',
      description: 'Sistem kullanıcı pozisyonlarını yönetin',
      keywords: ['kullanıcı', 'pozisyon', 'tip', 'tür', 'kategori', 'rol', 'type', 'tanımla', 'oluştur', 'ceo', 'müdür', 'uzman'],
      icon: UserIcon,
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('user-types'),
      adminOnly: true
    },
    {
      id: 'role-hierarchy',
      name: 'Pozisyon Hiyerarşisi',
      description: 'Pozisyon ve yönetici ilişkilerini tanımlayın',
      keywords: ['pozisyon', 'hiyerarşi', 'yönetici', 'ast', 'üst', 'organizasyon', 'manager', 'hierarchy', 'boss', 'şef'],
      icon: UsersIcon,
      // Simple advance mode hides the hierarchy category entirely — bu modda
      // herkesin yöneticisi otomatik Süper Admin, farklı bir hiyerarşi yok.
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('role-hierarchy') && user?.advance_mode !== 'simple',
      adminOnly: true
    },
    {
      id: 'advance-categories',
      name: 'Avans Kategorileri',
      description: 'Avans kategorilerini yönetin',
      keywords: ['avans', 'kategori', 'advance', 'category', 'ekle', 'sil', 'düzenle'],
      icon: FolderIcon,
      // Solo planında avans talebi yok — kategoriler de anlamsız, sidebar'da gizle.
      available: hasAdminPermissions() && !isSoloPlan(),
      adminOnly: true
    },
    {
      id: 'supplier-management',
      name: 'İşletme Yönetimi',
      description: 'İşletmelerinizi yönetin',
      keywords: ['işletme', 'supplier', 'vendor', 'firma', 'ekle', 'sil', 'düzenle', 'ülke'],
      icon: FolderIcon,
      // Solo'da sol menüde "İşletmeler" sayfası zaten var — Ayarlar
      // altında tekrar etmesine gerek yok.
      available: hasAdminPermissions() && !isSoloPlan(),
      adminOnly: true
    },
    {
      id: 'department-management',
      name: 'Departman Yönetimi',
      description: 'Şirket departmanlarını yönetin',
      keywords: ['departman', 'department', 'bölüm', 'birim', 'ekle', 'sil', 'düzenle'],
      icon: BuildingIcon,
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('department-management'),
      adminOnly: true
    },
    {
      id: 'expense-category-management',
      name: 'Harcama Kategorileri',
      description: 'Harcama kategorileri ve alt kategorilerini yönetin',
      keywords: ['harcama', 'kategori', 'expense', 'category', 'alt', 'sub', 'ekle', 'sil', 'düzenle'],
      icon: FolderIcon,
      available: hasAdminPermissions(),
      adminOnly: true
    },
    {
      id: 'library',
      name: 'Kütüphane',
      description: 'Para birimleri ve ülkeleri yönetin',
      keywords: ['kütüphane', 'library', 'para', 'birimi', 'currency', 'döviz', 'euro', 'dolar', 'tl', 'pound', 'ülke', 'country', 'countries'],
      icon: FileTextIcon,
      available: hasAdminPermissions(),
      adminOnly: true
    },
    {
      id: 'user-permissions',
      name: 'Kullanıcı Yetkileri',
      description: 'Kullanıcılara özel modül ve işlem yetkilerini yönetin',
      keywords: ['yetki', 'izin', 'permission', 'erişim', 'modül', 'işlem', 'authorize', 'access', 'role', 'rol'],
      icon: ShieldIcon,
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('user-permissions'),
      adminOnly: true
    },
    {
      id: 'document-permissions',
      name: 'Belge Erişim Yetkileri',
      description: 'Kullanıcıların belge görüntüleme yetkilerini yönetin',
      keywords: ['belge', 'document', 'erişim', 'görme', 'yetki', 'permission', 'view', 'access'],
      icon: FileTextIcon,
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('document-permissions'),
      adminOnly: true
    },
    {
      id: 'payment-permissions',
      name: 'Ödeme İşleme Yetkileri',
      description: 'Kullanıcıların ödeme işleme yetkilerini yönetin',
      keywords: ['ödeme', 'payment', 'yetki', 'permission', 'process', 'muhasebe'],
      icon: DollarSign,
      available: hasAdminPermissions() && !isCategoryHiddenByPlan('payment-permissions'),
      adminOnly: true
    },
    {
      id: 'app-settings',
      name: 'Uygulama Ayarları',
      description: 'Uygulamanın genel görünümü ve entegrasyonları',
      keywords: ['uygulama', 'app', 'görünüm', 'tema', 'dil', 'entegrasyon', 'api', 'theme', 'language', 'integration'],
      icon: SettingsIcon,
      available: hasAdminPermissions(),
      adminOnly: true
    },
    {
      id: 'branding',
      name: 'Özel Markalama',
      description: 'Logo ve marka renklerinizi kaydedin',
      keywords: ['marka', 'markalama', 'brand', 'branding', 'logo', 'renk', 'color', 'tema', 'theme', 'özel', 'custom'],
      icon: PaletteIcon,
      // Solo tek kullanıcılı — özel markalama Solo için uygun değil.
      // Diğer planlarda: everyone sees it (admin edits, non-admins preview only).
      available: hasAdminPermissions() && !isSoloPlan(),
      adminOnly: true
    }
  ];

  // Filter categories based on search query
  const filteredCategories = settingsCategories.filter(category => {
    if (!category.available) return false;
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = category.name.toLowerCase().includes(query);
    const descMatch = category.description.toLowerCase().includes(query);
    const keywordMatch = category.keywords.some(keyword => keyword.toLowerCase().includes(query));
    
    return nameMatch || descMatch || keywordMatch;
  });

  const renderContent = () => {
    switch (activeCategory) {
      case 'profile':
        return <GeneralSettings user={user} type="profile" />;
      case 'security':
        return <SecuritySettings />;
      case 'sandbox-mode':
        return <SandboxModeSettings />;
      case 'solo-demo-data':
        return <SoloDemoDataSettings />;
      case 'company':
        return <GeneralSettings user={user} type="company" />;
      case 'advance-rules':
        return <AdvanceRulesSettings />;
      case 'user-management':
        return <UserManagementSettings />;
      case 'user-types':
        return <UserTypesSettings />;
      case 'role-hierarchy':
        return <SimpleRoleHierarchy />;
      case 'expense-categories':
        return <CategoryManagement />;
      case 'advance-categories':
        return <AdvanceCategoriesSettings />;
      case 'supplier-management':
        return <SupplierManagementSettings />;
      case 'department-management':
        return <DepartmentManagement />;
      case 'expense-category-management':
        return <ExpenseCategoryManagement />;
      case 'library':
        return <Library />;
      case 'user-permissions':
        return <UserPermissionsSettings />;
      case 'document-permissions':
        return <UserPermissions />;
      case 'payment-permissions':
        return <PaymentPermissions />;
      case 'app-settings':
        return (
          <div>
            <ApplicationSettings />
            {/* Para Birimleri Yönetimi — Şirket Bilgileri altından buraya
                taşındı (kullanıcının isteği). Aynı state ve backend endpoint. */}
            <GeneralSettings user={user} type="app-currencies" />
          </div>
        );
      case 'branding':
        return <BrandingSettings />;
      case 'credit-cards':
        return <CreditCardsSettings />;
      default:
        return <GeneralSettings user={user} type="profile" />;
    }
  };

  return (
    <div className="min-h-screen md:h-screen flex flex-col md:flex-row" data-testid="settings-page">
      {/* Sidebar */}
      <div className="w-full md:w-80 bg-white border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Ayarlar</h2>
          <p className="text-sm text-gray-600 mt-1">Sistem ayarlarını yönetin</p>
          
          {/* Search Input */}
          <div className="mt-4 relative">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Ayarlarda ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors z-10"
                  aria-label="Aramayı temizle"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchQuery && filteredCategories.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 z-10">
                Sonuç bulunamadı
              </div>
            )}
          </div>
        </div>
        
        {/* Categories */}
        <div className="md:flex-1 max-h-72 md:max-h-none overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-1 gap-2">
          {filteredCategories.map((category) => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`w-full text-left p-4 rounded-lg transition-all ${
                  activeCategory === category.id
                    ? 'bg-teal-50 border-2 border-teal-200 text-teal-900'
                    : 'hover:bg-gray-50 border-2 border-transparent text-gray-700'
                }`}
                data-testid={`settings-category-${category.id}`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    activeCategory === category.id ? 'bg-teal-100' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-4 h-4 ${
                      activeCategory === category.id ? 'text-teal-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h3 className="font-medium text-sm">{category.name}</h3>
                      {category.adminOnly && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Admin
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{category.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* User Info */}
        <div className="hidden md:block p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-teal-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{user?.user?.full_name}</p>
              <p className="text-xs text-gray-500">{user?.role?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default Settings;